import { type ReactNode, useCallback, useState } from 'react';

import { ReviewModal } from '@/components/csv-import/ReviewModal';
import { ProviderLogoTile } from '@/components/integrations/ProviderBrandMarks';
import { Button } from '@/lib/base/buttons/button';
import { CARD_BORDER } from '@/lib/base/list-card';
import type { CsvImportStatus } from '@/lib/csv-import/api';
import { cx } from '@/utils/cx';

export interface VendorImportWizardHandoff {
  orgId: number;
  isOpen: boolean;
  onClose: () => void;
  onImportCreated: (importId: number, status: CsvImportStatus) => void;
}

export interface VendorImportCardProps {
  orgId: number | undefined;
  title: string;
  description: string;
  ctaLabel: string;
  icon: ReactNode;
  renderWizard: (handoff: VendorImportWizardHandoff) => ReactNode;
  showCta?: boolean;
}

export function VendorImportCard({
  orgId,
  title,
  description,
  ctaLabel,
  icon,
  renderWizard,
  showCta = true,
}: VendorImportCardProps) {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [createdImport, setCreatedImport] = useState<{
    id: number;
    status: CsvImportStatus;
  } | null>(null);

  const openWizard = useCallback(() => {
    setIsWizardOpen(true);
  }, []);

  const closeWizard = useCallback(() => {
    setIsWizardOpen(false);
  }, []);

  const handleImportCreated = useCallback(
    (importId: number, status: CsvImportStatus) => {
      setIsWizardOpen(false);
      setCreatedImport({ id: importId, status });
    },
    []
  );

  const handleReviewClose = useCallback(() => {
    setCreatedImport(null);
  }, []);

  return (
    <>
      <div className={cx(CARD_BORDER, 'bg-bg-secondary p-6')}>
        <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
          <div className='flex items-start gap-3'>
            <ProviderLogoTile>{icon}</ProviderLogoTile>
            <div>
              <h3 className='text-sm font-medium text-fg-primary'>{title}</h3>
              <p className='text-sm text-fg-secondary mt-1'>{description}</p>
            </div>
          </div>
          {showCta && (
            <Button
              color='primary'
              onClick={openWizard}
              isDisabled={!orgId}
              className='self-start sm:self-auto shrink-0'
            >
              {ctaLabel}
            </Button>
          )}
        </div>
      </div>

      {orgId &&
        renderWizard({
          orgId,
          isOpen: isWizardOpen,
          onClose: closeWizard,
          onImportCreated: handleImportCreated,
        })}

      {orgId && createdImport && (
        <ReviewModal
          orgId={orgId}
          importId={createdImport.id}
          initialStatus={createdImport.status}
          isOpen
          onClose={handleReviewClose}
        />
      )}
    </>
  );
}
